import {
  LeftArrowCircleIcon,
  ReloadIcon,
  RightArrowCircleIcon,
  TextBubbleIcon,
} from '@sketchnote/icons';
import { Avatar, AvatarGroup, Button } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import { format, parseISO } from 'date-fns';
import * as React from 'react';
import { useAuth } from 'src/shared/hooks/use-auth';
import { getMemberDetails } from 'src/utils/getMemberDetails';

import { useTasksStore } from '../../store/useTasksStore';
import { TaskT } from '../../types/tasks';

const { useState, useEffect, useMemo } = React;

export const ApprovalsHistory = ({ item: task }: { item: TaskT }) => {
  const { setShowHistory, onWithdrawTaskApproval } = useTasksStore((state) => state);
  const handleWithdrawRequest = () => {
    //hanlde withdraw
    if (task?.id) {
      onWithdrawTaskApproval(task?.id);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <section className="flex flex-col flex-grow py-8 overflow-auto">
        <HistoryLog item={task} />
        <ActionsCarousal item={task} />
      </section>

      <section className="pt-14  px-12 items-center flex justify-between border-t bg-gray-199">
        <AvatarGroup maxCount={5} className="mr-13">
          {task?.approval?.approvers && task?.approval?.approvers?.length > 0 ? (
            task?.approval?.approvers.map((approver, index) => {
              return (
                <Avatar
                  key={index}
                  size="sm1"
                  name={getMemberDetails(approver.member)?.firstname || 'User'}
                  src={getMemberDetails(approver.member)?.avatar}
                  type="initials"
                  wrapperClassName={`border-2 ${
                    approver.status === 'pending'
                      ? 'border-orange-300'
                      : approver.status === 'approved'
                      ? 'border-green-500'
                      : approver.status === 'rejected'
                      ? 'border-red-500'
                      : 'border-black'
                  }`}
                />
              );
            })
          ) : (
            <span />
          )}
        </AvatarGroup>
        <div>
          {!task?.approval?.isTaskWithdrawn && task?.approval?.approvalCycle !== 'completed' ? (
            <Button status="secondary" onClick={handleWithdrawRequest}>
              Withdraw request
            </Button>
          ) : (
            <></>
          )}
          <Button className="ml-6" onClick={() => setShowHistory(false)}>
            {task?.approval?.isTaskWithdrawn || task?.approval?.approvalCycle === 'completed'
              ? 'Restart approval'
              : 'Edit approvers'}
          </Button>
        </div>
      </section>
    </div>
  );
};

export const HistoryLog = ({ item: task }: { item: TaskT }) => {
  const { onResendRequest } = useTasksStore((state) => state);

  const onhandleResendRequest = (member: string) => {
    if (task?.id) {
      onResendRequest(task?.id, member);
    }
  };
  const approvalHistoryTemplate = (currentMember, members, action, value) => {
    switch (action) {
      case 'update_add_approvers':
        return (
          <span>
            {currentMember?.firstname || 'User'} added{' '}
            {members.length !== 0
              ? members?.length > 1
                ? `${members?.map((obj) => obj.firstname).join(', ')} as approvers`
                : `${members[0]?.firstname} as approver`
              : 'approvers'}
          </span>
        );
      case 'withdraw_request':
        return <span>{currentMember?.firstname || 'User'} withdrew approval request</span>;
      case 'resolve_request_change':
        return (
          <span>
            {currentMember?.firstname || 'User'} has resolved a change{' '}
            {members[0]?.firstname
              ? `requested by
            ${members[0]?.firstname}`
              : ''}
          </span>
        );
      case 'request_change':
        return <span>{currentMember?.firstname || 'User'} has raised a change request</span>;
      case 'approval_completed':
        return (
          <div className="flex justify-between w-full">
            <div>
              {currentMember?.firstname || 'User'} has {value} the request
              <span
                className={`${ApprovalTagColor[value]} text-xs px-6 font-medium border mb-4 rounded-full ml-4 inline-block capitalize`}
              >
                {value}
              </span>
            </div>
            <Tippy
              arrow={false}
              content={
                <div className="text-xs text-gray-999">
                  <div className="mb-4">Resend request</div>
                  <p>Approver will be notified to reconsider the decision</p>
                </div>
              }
              className="w-200 bg-white border rounded-lg p-4"
            >
              <button onClick={() => onhandleResendRequest(currentMember?.memberId)}>
                <ReloadIcon wrapperClass="text-sm mr-4 text-gray-999" />
              </button>
            </Tippy>
          </div>
        );

      case 'reassign_approver':
        return (
          <span>
            {currentMember?.firstname || 'User'} has reassigned the request to{' '}
            {members[0]?.firstname}
          </span>
        );

      case 'update_remove_approvers':
        return (
          <span>
            {currentMember?.firstname || 'User'} removed{' '}
            {members.length !== 0
              ? members?.length > 1
                ? members?.map((obj) => obj.firstname).join(', ')
                : `${members[0]?.firstname} as ${members?.length > 0 ? 'approver' : 'approvers'}`
              : 'approvers'}
          </span>
        );

      case 'final_status':
        return (
          <div>
            Task is {value}
            <span
              className={`${ApprovalTagColor[value]} text-xs px-6 font-medium border mb-4 rounded-full ml-4 inline-block capitalize`}
            >
              {value}
            </span>
          </div>
        );
      case 'update_minimum_approvals':
        return (
          <span>
            {currentMember?.firstname || 'User'} has updated the minimum required approvals to{' '}
            {value}
          </span>
        );
      default:
        return <div />;
    }
  };

  const approvaHistoryContainer = document.getElementById('approval_history');
  useEffect(() => {
    if (
      task?.approval?.approvalHistory &&
      task?.approval?.approvalHistory?.length > 0 &&
      approvaHistoryContainer
    ) {
      approvaHistoryContainer.scrollTo({
        top: approvaHistoryContainer.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [task?.approval?.approvalHistory?.length]);

  return (
    <div className="px-12 w-full text-xs flex-grow overflow-auto" id="approval_history">
      {task?.approval?.approvalCycle !== 'completed' ? (
        task?.approval?.approvers?.map((obj, index) =>
          obj.status === 'pending' ? (
            <div
              className="flex items-center mb-12 text-xs text-gray-999"
              key={`${index}-${obj.member}`}
            >
              <Avatar
                size="sm1"
                name={getMemberDetails(obj.member)?.firstname || 'User'}
                src={getMemberDetails(obj.member)?.avatar}
                type="initials"
              />

              <div className="ml-12">
                {getMemberDetails(obj.member)?.firstname || 'User'} is yet to make a decision
              </div>
              <span
                style={{
                  color: '#B45309',
                  backgroundColor: '#FEF4C7',
                  border: '1px solid #FDE68A',
                }}
                className="items-center px-4 rounded-full ml-4"
              >
                Pending
              </span>
            </div>
          ) : (
            <span />
          )
        )
      ) : (
        <></>
      )}

      {task?.approval?.approvalHistory?.map((historyItem, index) => {
        const currentMember = getMemberDetails(historyItem?.member);
        const members = historyItem?.members?.map((memberId) => getMemberDetails(memberId)) || [];

        return (
          <div className="flex text-xs mb-12 w-full" key={index}>
            <div>
              {historyItem?.action !== 'final_status' && (
                <Avatar
                  size="sm1"
                  name={currentMember?.firstname || 'User'}
                  src={currentMember?.avatar}
                  type="initials"
                />
              )}
            </div>
            <div className="ml-12 w-full">
              <span className="inline-block w-full">
                {approvalHistoryTemplate(
                  currentMember,
                  members.filter((obj) => Object.keys(obj).length > 0),
                  historyItem?.action,
                  historyItem?.value
                )}
              </span>
              {historyItem?.comment && (
                <div className="p-8 w-full rounded-lg text-gray-999 bg-gray-100">
                  {historyItem?.comment}
                </div>
              )}
              {historyItem?.timestamp && (
                <div className="py-4 rounded-lg text-xss" style={{ color: '#9CA3AF' }}>
                  {format(parseISO(historyItem?.timestamp), 'MMM d, yyyy hh:mm aa')}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const ActionsCarousal = ({ item: task }: { item: TaskT }) => {
  const [index, setIndex] = useState(0);
  const [decisionPending, setDescisionPending] = useState<JSX.Element[]>([]);
  const [changeRequests, setChangeRequests] = useState<JSX.Element[]>([]);
  const authData = useAuth((s) => s.userDetails);

  const carousalArray = useMemo(() => {
    if (task?.approval?.isTaskWithdrawn) {
      return [];
    } else {
      return decisionPending.concat(changeRequests);
    }
  }, [task?.approval?.isTaskWithdrawn, decisionPending, changeRequests]);

  //Updates Decision pending
  useEffect(() => {
    setIndex(0);
    if (task?.approval?.approvers && task?.approval?.approvalCycle !== 'completed') {
      const isCurrentMemberApprover =
        task?.approval?.approvers?.filter(
          (obj) => obj.member === authData?.memberInformation?.memberId && obj.status === 'pending'
        ).length > 0;

      if (isCurrentMemberApprover) {
        setDescisionPending([<DecisionPending item={task} key={index} />]);
      } else {
        setDescisionPending([]);
      }
    } else if (task?.approval?.approvalCycle === 'completed') {
      setDescisionPending([]);
    }
  }, [task?.approval?.approvers]);

  useEffect(() => {
    setIndex(0);

    const tempChangeRequest: JSX.Element[] = [];
    if (
      task?.approval?.approvalCycle !== 'completed' &&
      task?.approval?.changeRequests &&
      task?.approval?.changeRequests.length > 0 &&
      task?.approval?.approvalRequestedBy === authData?.memberInformation?.memberId
    ) {
      task?.approval?.changeRequests.map((request, index) => {
        tempChangeRequest.push(
          <ChangesRequested item={task} changeRequest={request} key={index} />
        );
      });

      setChangeRequests(tempChangeRequest);
    } else {
      setChangeRequests([]);
    }
  }, [task?.approval?.changeRequests]);

  return carousalArray?.length > 0 ? (
    <div className="w-full">
      <div className="flex justify-between mx-12">
        <span className="text-gray-800 text-xs inline">
          {index + 1}/{carousalArray.length}
        </span>
        <div className="flex">
          <button
            className="inline mr-8"
            disabled={index === 0}
            onClick={() => {
              if (index > 0) {
                setIndex((index) => index - 1);
              }
            }}
          >
            <LeftArrowCircleIcon
              className={`${index === 0 ? 'text-gray-500' : 'text-black'} text-xl`}
            />
          </button>
          <button
            className="inline"
            disabled={index === carousalArray.length - 1}
            onClick={() => {
              if (index < carousalArray.length - 1) {
                setIndex((index) => index + 1);
              }
            }}
          >
            <RightArrowCircleIcon
              className={`${
                index === carousalArray.length - 1 ? 'text-gray-500' : 'text-black'
              } text-xl`}
            />
          </button>
        </div>
      </div>
      <div
        style={{
          transition: 'transform 0.3s ease-out',
          transform: `translate(-${index * 100}%)`,
        }}
        className="flex w-full"
      >
        {carousalArray.map((item, _index) => {
          return (
            <div key={_index} className="w-full">
              {item}
            </div>
          );
        })}
      </div>
    </div>
  ) : (
    <span />
  );
};

export const DecisionPending = ({ item: task }: { item: TaskT }) => {
  const { onSetTaskApprovalStatus, onRequestChanges } = useTasksStore((state) => state);
  const [comments, setComments] = useState<string>('');
  const authData = useAuth((s) => s.userDetails);
  const handleRequestChanges = () => {
    if (task?.id) {
      if (comments.trim().length === 0) return;
      onRequestChanges(task?.id, comments);
      setComments('');
    }
  };

  const handleUpdateTaskStatus = (status: string) => {
    if (task?.id && authData?.memberInformation?.memberId) {
      onSetTaskApprovalStatus(task?.id, status, authData?.memberInformation?.memberId, comments);
      setComments('');
    }
  };
  //
  return (
    <div className="h-166 bg-gray-100 p-12 border rounded-sm mx-12 mt-10 w-374">
      <span className="block text-gray-999 text-xs">Task decision pending</span>

      <span className="block text-gray-750 text-xs">You are yet to make a decision</span>
      <input
        className="p-6 rounded-lg border focus:border-blue-300 w-full my-10 text-xs"
        onChange={(e) => setComments(e?.target?.value)}
        value={comments}
      />
      <div className="flex">
        <Button status="secondary-gray" size="sm" onClick={handleRequestChanges}>
          Request change
        </Button>
        <Button
          status="danger-alt"
          className="mx-8"
          size="sm"
          onClick={() => handleUpdateTaskStatus('rejected')}
        >
          Reject
        </Button>
        <Button status="primary" size="sm" onClick={() => handleUpdateTaskStatus('approved')}>
          Approve
        </Button>
      </div>
      {task?.approval?.approvalRequestedBy && (
        <div className="italics text-xss text-gray-600 py-10 flex items-center">
          <Avatar
            size="xs1"
            name={getMemberDetails(task?.approval?.approvalRequestedBy)?.firstname}
            src={getMemberDetails(task?.approval?.approvalRequestedBy)?.avatar}
            type="initials"
          />
          <span className="ml-2 inline">
            {getMemberDetails(task?.approval?.approvalRequestedBy)?.firstname || 'User'} will
            receive change requests
          </span>
        </div>
      )}
    </div>
  );
};

export const ChangesRequested = ({
  item: task,
  changeRequest,
}: {
  item: TaskT;
  changeRequest: { member: string; _id: string; comment?: string };
}) => {
  const { onResolveRequest } = useTasksStore((state) => state);
  const [comments, setComments] = useState<string>('');

  const handleResolveRequestChanges = () => {
    if (task?.id) {
      onResolveRequest(task?.id, comments, changeRequest?._id);
      setComments('');
    }
  };
  const changesRequestedBy = getMemberDetails(changeRequest?.member);
  return (
    <div className="h-166 bg-gray-100 p-12 border rounded-sm mx-12 mt-10 w-374">
      <div className="flex justify-between">
        <div>
          <span className="block text-gray-999 text-xs font-medium">
            {changesRequestedBy?.firstname
              ? `Changes have been requested by ${changesRequestedBy?.firstname}`
              : 'Changes have been requested'}
          </span>
          <div className="flex">
            <span className="block text-gray-750 text-xs mr-4">Decision is pending</span>
            {changeRequest?.comment ? (
              <Tippy
                arrow={false}
                content={<div className="text-xs text-gray-999">{changeRequest?.comment}</div>}
                className="w-200 bg-white border rounded-lg p-4"
              >
                <span>
                  <TextBubbleIcon />
                </span>
              </Tippy>
            ) : (
              <></>
            )}
          </div>
        </div>
        {changesRequestedBy?.firstname && (
          <Avatar
            size="sm1"
            name={changesRequestedBy?.firstname || 'User'}
            src={changesRequestedBy?.avatar}
            type="initials"
            wrapperClassName="border-2 border-orange-300"
          />
        )}
      </div>
      <input
        className="p-6 rounded-lg border focus:border-blue-300 w-full my-10 text-xs"
        onChange={(e) => setComments(e?.target?.value)}
        value={comments}
      />

      <Button status="primary" size="sm" onClick={handleResolveRequestChanges}>
        Resolve
      </Button>
    </div>
  );
};

export const ApprovalTagColor = {
  approved: 'bg-green-150 border-green-175 text-green-750',
  rejected: 'bg-red-150 border-red-175 text-red-750',
  pending: 'bg-yellow-175 border-yellow-250 text-yellow-950',
};

export const ApprovalMessage = {
  approved: '🎉 Approved',
  rejected: '🚫 Rejected',
  pending: '⌛ Pending',
};
